/**
 * MergeBranchConditionPill — inline-editable text for the `AND <cond>`
 * predicate after `WHEN [NOT] MATCHED`. Click to edit, Enter / blur commits
 * via `modifyMergeBranchCondition`, Escape reverts. Mirrors the
 * FilterEditablePill / JoinEditableOnPill / AssignmentEditableValuePill
 * pattern.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.
 */

import { useEffect, useRef, useState } from 'react';

import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import {
  modifyMergeBranchCondition,
  type MergeBranchSelector,
} from '../../edit/edits/modifyMergeBranch';

export interface MergeBranchConditionPillProps {
  readonly testId: string;
  readonly text: string;
  readonly selector: MergeBranchSelector;
  readonly editContext: VqeEditContext;
}

export function MergeBranchConditionPill({
  testId,
  text,
  selector,
  editContext,
}: MergeBranchConditionPillProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setValue(text);
  }, [text, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = (): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed === text.trim()) {
      setEditing(false);
      setValue(text);
      return;
    }
    setEditing(false);
    void modifyMergeBranchCondition(selector, trimmed, toCommitCtx(editContext));
  };

  const cancel = (): void => {
    setEditing(false);
    setValue(text);
  };

  if (!editing) {
    return (
      <VqeTooltip content="Click to edit branch condition">
        <button
          type="button"
          className="rounded-md border border-border bg-muted/30 px-2 py-0.5 font-mono hover:underline"
          data-testid={testId}
          onClick={() => setEditing(true)}
        >
          {text}
        </button>
      </VqeTooltip>
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      data-testid={`${testId}-input`}
      className="rounded border border-input bg-background px-1.5 py-0.5 font-mono"
      aria-label="Edit MERGE branch condition"
    />
  );
}
